import type { ReviiriClub, ReviiriClubsResult, ReviiriConnectResult, ReviiriImportResult } from '@wehunt/ui'
import { apiFetch } from '../../../shared/api/apiFetch'
import { ApiError } from '../../../shared/api/ApiError'

/**
 * Reviiri import (CLUB-191). Three calls, one per step of the flow: connect an account,
 * list what it can see, import one map's boundary. The Reviiri token itself never
 * reaches the browser — the API stores it on the user document and uses it server-side,
 * so every call here is authorized by the caller's Firebase token alone.
 */

interface ConnectResponse {
  email: string
}

interface ClubsResponse {
  clubs?: { id: string; name: string; maps?: { id: string; name: string }[] }[]
}

interface ImportResponse {
  id: string
  status: 'created' | 'refreshed'
}

const errorCode = (err: ApiError): string | null =>
  typeof err.problem.code === 'string' ? err.problem.code : null

/** Connects (or re-connects) the signed-in user's Reviiri account. */
export async function connectReviiri(
  email: string,
  password: string,
  token: string,
): Promise<ReviiriConnectResult> {
  try {
    const res = await apiFetch<ConnectResponse>('/clubhouse/reviiri/session', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      token,
    })
    return { status: 'connected', email: res.email }
  } catch (err) {
    if (err instanceof ApiError) {
      // Only the handler's explicit provider code means the submitted Reviiri password
      // was rejected. An uncoded 401 can come from Firebase JWT middleware before this
      // endpoint runs and must not blame credentials the provider never received.
      if (errorCode(err) === 'reviiri.login_rejected') return { status: 'rejected' }
      return { status: 'error', code: errorCode(err) }
    }
    throw err
  }
}

/** Lists the clubs and maps the connected account can see. */
export async function fetchReviiriClubs(token: string): Promise<ReviiriClubsResult> {
  try {
    const res = await apiFetch<ClubsResponse>('/clubhouse/reviiri/clubs', {
      method: 'GET',
      token,
    })
    const clubs: ReviiriClub[] = (res.clubs ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      maps: (c.maps ?? []).map((m) => ({ id: m.id, name: m.name })),
    }))
    return { status: 'ok', clubs }
  } catch (err) {
    if (err instanceof ApiError) {
      // These two provider codes share one recovery path. A generic 401 belongs to the
      // app's Firebase session instead and remains an ordinary error.
      const code = errorCode(err)
      if (code === 'reviiri.not_connected' || code === 'reviiri.login_expired')
        return { status: 'not-connected' }
      return { status: 'error', code: errorCode(err) }
    }
    throw err
  }
}

/** Imports one Reviiri map's boundary into a team's hunting ground. */
export async function importReviiriMap(
  groupId: string,
  mapId: string,
  token: string,
): Promise<ReviiriImportResult> {
  try {
    const res = await apiFetch<ImportResponse>(
      `/clubhouse/groups/${encodeURIComponent(groupId)}/hunting-grounds/import/reviiri`,
      {
        method: 'POST',
        // The map id goes in the BODY, not the path — it contains a slash, and
        // percent-encoding it into a route segment is what would break it.
        body: JSON.stringify({ mapId }),
        token,
      },
    )
    return { status: res.status }
  } catch (err) {
    if (err instanceof ApiError) {
      const code = errorCode(err)
      if (code === 'reviiri.not_connected' || code === 'reviiri.login_expired')
        return { status: 'not-connected' }
      if (code === 'reviiri.map_not_found') return { status: 'not-found' }
      return { status: 'error', code: errorCode(err) }
    }
    throw err
  }
}
